// hooks/useObjectives.js - Hook corrigé pour afficher tous les objectifs
import { useState, useCallback } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export const useObjectives = (userId) => {
  // ================================
  // ÉTATS
  // ================================
  
  const [availableObjectives, setAvailableObjectives] = useState([]);
  const [loadingObjectives, setLoadingObjectives] = useState(false);
  const [objectivesError, setObjectivesError] = useState('');

  // ================================
  // FONCTIONS UTILITAIRES
  // ================================

  // Calcul de similarité pour sélection d'objectifs
  const calculateSimilarity = (query, objective) => {
    const queryLower = query.toLowerCase();
    const titleLower = objective.title.toLowerCase();
    const themeLower = (objective.theme || '').toLowerCase();
    const descriptionLower = (objective.description || '').toLowerCase();
    
    // Score de correspondance directe dans le titre
    let titleScore = 0;
    if (titleLower.includes(queryLower)) {
      titleScore = 0.8;
    } else {
      const queryWords = queryLower.split(' ').filter(w => w.length > 2);
      const titleWords = titleLower.split(' ').filter(w => w.length > 2);
      const commonWords = queryWords.filter(word => 
        titleWords.some(tw => tw.includes(word) || word.includes(tw))
      );
      titleScore = (commonWords.length / Math.max(queryWords.length, 1)) * 0.6;
    }
    
    // Score de correspondance dans le thème
    let themeScore = 0;
    if (themeLower.includes(queryLower)) {
      themeScore = 0.3;
    }
    
    // Score de correspondance dans la description
    let descriptionScore = 0;
    if (descriptionLower.includes(queryLower)) {
      descriptionScore = 0.2;
    }
    
    // Score bonus pour les objectifs actifs
    let activityScore = 0;
    if (objective.phase === 'action') activityScore = 0.1;
    else if (objective.phase === 'stratégie') activityScore = 0.05;
    
    return titleScore + themeScore + descriptionScore + activityScore;
  };

  // 🔧 FIX 3: Fonction améliorée pour filtrer et afficher TOUS les objectifs
  const getFilteredObjectives = useCallback((searchQuery = '') => {
    if (!searchQuery.trim()) {
      // 🔧 FIX: Retourner TOUS les objectifs sans limitation, triés par pertinence
      return availableObjectives
        .sort((a, b) => {
          // Prioriser par phase (action > stratégie > ambition)
          const phaseOrder = { 'action': 3, 'stratégie': 2, 'ambition': 1 };
          const aPhase = phaseOrder[a.phase] || 0;
          const bPhase = phaseOrder[b.phase] || 0;
          
          if (aPhase !== bPhase) return bPhase - aPhase;
          
          // Puis par performance
          return (b.performance || 0) - (a.performance || 0);
        });
    }
    
    // Si recherche active, filtrer mais garder tous les résultats pertinents
    const scoredObjectives = availableObjectives.map(obj => ({
      ...obj,
      similarity: calculateSimilarity(searchQuery, obj)
    })).filter(obj => obj.similarity > 0.05) // Seuil plus bas pour plus de résultats
      .sort((a, b) => b.similarity - a.similarity);
    
    // 🔧 FIX: Ne pas limiter les résultats de recherche
    return scoredObjectives;
  }, [availableObjectives]);

  // ================================
  // 🔧 FIX 3: CHARGEMENT OPTIMISÉ DE LA COLLECTION "competences"
  // ================================

  const loadUserObjectives = useCallback(async () => {
    if (!userId) return;

    try {
      setLoadingObjectives(true);
      setObjectivesError('');
      
      console.log('📝 Chargement des compétences depuis la collection "competences"...');
      
      // 🔧 FIX: Chargement direct de la collection "competences"
      const competencesCollection = collection(db, 'users', userId, 'competences');
      const competencesSnapshot = await getDocs(competencesCollection);
      
      if (competencesSnapshot.empty) {
        console.log('⚠️ Aucune compétence trouvée dans la collection "competences"');
        setAvailableObjectives([]);
        return;
      }

      const collectionSize = competencesSnapshot.size;
      console.log(`✅ Trouvé ${collectionSize} compétences dans la collection "competences"`);
      
      // 🔧 FIX: Mapping exhaustif des données de compétences
      const objectives = competencesSnapshot.docs.map(doc => {
        const data = doc.data();
        
        // Log des données pour debugging
        console.log(`📋 Compétence ${doc.id}:`, {
          shortTitle: data.shortTitle,
          title: data.title,
          chapitre: data.chapitre,
          phase: data.phase,
          performance: data.performance
        });
        
        return {
          id: doc.id,
          // 🔧 FIX: Mapping spécifique pour les compétences
          title: data.shortTitle || data.title || data.name || data.objectif || 'Compétence sans titre',
          theme: data.chapitre || data.theme || data.categorie || 'Non catégorisé',
          type: data.type || 'competence',
          performance: Number(data.performance) || Number(data.globalProgress) || Number(data.progress) || 0,
          phase: data.phase || data.status || data.etat || 'ambition',
          description: data.description || data.resume || data.summary || data.longTitle || '',
          createdAt: data.createdAt || data.dateCreation || data.created || new Date().toISOString(),
          
          // Métadonnées supplémentaires spécifiques aux compétences
          priority: data.priority || data.priorite || 'normal',
          difficulty: data.difficulty || data.difficulte || 'medium',
          category: data.category || data.categorie || data.chapitre,
          tags: data.tags || data.mots_cles || [],
          lastUpdated: data.lastUpdated || data.lastModified || data.updated_at,
          
          // Données spécifiques aux compétences
          level: data.level || data.niveau || 'débutant',
          targetDate: data.targetDate || data.dateObjectif || data.echeance,
          resources: data.resources || data.ressources || [],
          
          // Source pour debugging
          sourceCollection: 'competences',
          rawData: data // 🔧 TEMPORAIRE: pour debugging
        };
      }).filter(obj => obj.title && obj.title.trim() !== '' && obj.title !== 'Compétence sans titre');

      // 🔧 FIX: Trier TOUTES les compétences par importance
      objectives.sort((a, b) => {
        // Prioriser par phase
        const phaseOrder = { 'action': 3, 'stratégie': 2, 'ambition': 1 };
        const aPhase = phaseOrder[a.phase] || 0;
        const bPhase = phaseOrder[b.phase] || 0;
        
        if (aPhase !== bPhase) return bPhase - aPhase;
        
        // Puis par performance
        const aPerf = Number(a.performance) || 0;
        const bPerf = Number(b.performance) || 0;
        if (aPerf !== bPerf) return bPerf - aPerf;
        
        // Puis par date de création (plus récent en premier)
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      setAvailableObjectives(objectives);
      
      console.log(`✅ ${objectives.length} compétences chargées avec succès`);
      
      // 🔧 FIX: Statistiques détaillées
      const stats = objectives.reduce((acc, obj) => {
        acc.byPhase[obj.phase] = (acc.byPhase[obj.phase] || 0) + 1;
        acc.byTheme[obj.theme] = (acc.byTheme[obj.theme] || 0) + 1;
        return acc;
      }, { byPhase: {}, byTheme: {} });
      
      console.log('📊 Répartition des compétences:', stats);
      
      // Log des premières compétences pour vérification
      if (objectives.length > 0) {
        console.log('📋 Premières compétences:', 
          objectives.slice(0, 5).map(obj => ({
            id: obj.id,
            titre: obj.title,
            theme: obj.theme,
            phase: obj.phase,
            performance: obj.performance
          }))
        );
      }
      
    } catch (error) {
      console.error('❌ Erreur chargement compétences:', error);
      setObjectivesError(`Erreur de chargement des compétences: ${error.message}`);
      setAvailableObjectives([]);
    } finally {
      setLoadingObjectives(false);
    }
  }, [userId]);

  // ================================
  // 🔧 FIX: FONCTIONS UTILITAIRES OPTIMISÉES
  // ================================

  // Fonction pour obtenir les statistiques des compétences
  const getObjectivesStats = useCallback(() => {
    if (availableObjectives.length === 0) return null;
    
    const stats = {
      total: availableObjectives.length,
      byPhase: {},
      byTheme: {},
      averagePerformance: 0,
      recentlyUpdated: 0
    };
    
    let totalPerformance = 0;
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    
    availableObjectives.forEach(obj => {
      // Par phase
      stats.byPhase[obj.phase] = (stats.byPhase[obj.phase] || 0) + 1;
      
      // Par thème
      if (obj.theme) {
        stats.byTheme[obj.theme] = (stats.byTheme[obj.theme] || 0) + 1;
      }
      
      // Performance totale
      totalPerformance += Number(obj.performance) || 0;
      
      // Récemment mises à jour
      const lastUpdate = new Date(obj.lastUpdated || obj.createdAt);
      if (lastUpdate > weekAgo) {
        stats.recentlyUpdated++;
      }
    });
    
    // Performance moyenne
    stats.averagePerformance = availableObjectives.length > 0 
      ? Math.round(totalPerformance / availableObjectives.length) 
      : 0;
    
    return stats;
  }, [availableObjectives]);

  // Fonction pour rechercher des compétences par mot-clé
  const searchObjectives = useCallback((searchTerm) => {
    if (!searchTerm || !searchTerm.trim()) return availableObjectives;
    
    const term = searchTerm.toLowerCase();
    return availableObjectives.filter(obj => 
      obj.title.toLowerCase().includes(term) ||
      obj.theme.toLowerCase().includes(term) ||
      obj.description.toLowerCase().includes(term) ||
      (obj.tags && obj.tags.some(tag => tag.toLowerCase().includes(term)))
    );
  }, [availableObjectives]);

  // ================================
  // RETOUR DU HOOK AMÉLIORÉ
  // ================================

  return {
    // Données principales
    availableObjectives,
    loadingObjectives,
    objectivesError,
    
    // Fonctions
    loadUserObjectives,
    getFilteredObjectives,
    
    // 🔧 NOUVELLES FONCTIONS
    getObjectivesStats,
    searchObjectives,
    
    // Métadonnées utiles
    totalObjectives: availableObjectives.length,
    hasObjectives: availableObjectives.length > 0
  };
};